// libs
import React, { memo, useMemo, useEffect, useState } from "react";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";

// contexts
import { useResponsiveContext } from "shared/components/ResponsiveLoader/ResponsiveContext";

// styles
import { Image, Title, Info, Section, Anchor, ImagePlaceholder } from "./styles";

// helpers
import { getImageData } from "./helpers";
import { generateTrackingUrl } from "shared/components/AdPlatformWidgets/helpers";
import { getPlaceHolderColor } from "components/AdPlatformWidgets/helpers";
import { generateSameHostUrl } from "shared/helpers/utils";

// constants
import { DWEB_MAX_WIDTH } from "shared/styles/base";
import { IMAGE_ALT } from "./constants";
import { MARGIN_RIGHT } from "shared/components/AdPlatformWidgets/Desktop/constant";
import { ON_LOAD_PLACEHOLDER_COLOR } from "shared/components/AdPlatformWidgets/constants";

// def
import { IWidgetItems } from "shared/components/AdPlatformWidgets/types";
interface ICardProps {
    widgetItem: IWidgetItems;
    numOfColumns: number;
    inventoryName: string;
    className?: string;
    childCount: number;
    contentAlign: string;
    textColor: string;
}

function Card({
    widgetItem,
    numOfColumns,
    inventoryName,
    childCount,
    className = "",
    contentAlign,
    textColor,
}: ICardProps) {
    const { deviceWidth } = useResponsiveContext();
    const [showImage, setShowImage] = useState(false);

    useEffect(() => {
        setShowImage(true);
    }, []);

    const containerWidth = deviceWidth > DWEB_MAX_WIDTH ? DWEB_MAX_WIDTH : deviceWidth;

    const action_url = generateTrackingUrl(
        widgetItem.parameters.web_action_url,
        inventoryName,
        widgetItem.positionInParent,
        widgetItem.parameters.tracking_parameters
    );

    const bannerData = getImageData(widgetItem, containerWidth, numOfColumns);
    const placeholderColor = getPlaceHolderColor(childCount);

    const title = get(widgetItem, "parameters.title") || get(widgetItem, "parameters.title_plain");
    const description = get(widgetItem, "parameters.description");

    const visibleMargin = (numOfColumns - 1) * MARGIN_RIGHT;

    const descriptionStyle = useMemo(() => ({ textAlign: contentAlign }), [contentAlign]);

    const goToUrl = (event: React.MouseEvent) => {
        event.preventDefault();
        window.open(generateSameHostUrl(action_url));
    };

    const canShowDescription = isEmpty(title) === false || isEmpty(description) === false;

    const Content = (
        <div>
            {showImage ? (
                <Image
                    src={bannerData.source}
                    width={(containerWidth - visibleMargin) / numOfColumns}
                    aspectRatio={bannerData.aspect_ratio}
                    placeholderColor={placeholderColor}
                    alt={IMAGE_ALT}
                    onLoadPlaceholderColor={ON_LOAD_PLACEHOLDER_COLOR}
                    // @ts-ignore
                    srcSet={`${bannerData.source} 1x, ${bannerData.originalSource} 2x`}
                />
            ) : (
                <ImagePlaceholder
                    bgColor={placeholderColor}
                    aspectRatio={bannerData.aspect_ratio}
                />
            )}

            {canShowDescription && (
                <div style={descriptionStyle}>
                    {title && <Title textColor={textColor}>{title}</Title>}
                    {description && <Info textColor={textColor}>{description}</Info>}
                </div>
            )}
        </div>
    );

    if (isEmpty(action_url)) {
        return (
            <Section numOfColumns={numOfColumns} className={className}>
                {Content}
            </Section>
        );
    }

    return (
        <Anchor
            href={action_url}
            aria-label={IMAGE_ALT}
            onClick={goToUrl}
            className={className}
            numOfColumns={numOfColumns}
        >
            {Content}
        </Anchor>
    );
}

export default memo(Card);
